import { sentenceCase, type StatusTone } from '@relay-towing/ui';

/** The street, without the city: "La Jolla Cir" from "La Jolla Cir, San Diego". */
export const firstLine = (address: string | null | undefined): string =>
  address?.split(',')[0]?.trim() || 'Call';

/** Everything after the street: "San Diego, CA 92101", under it on a call. */
export const restOfAddress = (
  address: string | null | undefined
): string | undefined =>
  address?.split(',').slice(1).join(',').trim() || undefined;

/** The town, under the street in a list: "San Diego" from "La Jolla Cir, San Diego, CA". */
export const locality = (
  address: string | null | undefined
): string | undefined => address?.split(',')[1]?.trim() || undefined;

/** "October 3 @ 3:39 PM", as the Figma writes a time on a call. */
export const dayAndTime = (iso: string): string => {
  const date = new Date(iso);
  const day = date.toLocaleDateString([], { month: 'long', day: 'numeric' });
  const time = date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${day} @ ${time}`;
};

/**
 * "October 14, 2026" and "12:42:28 PM", apart, as the Figma's Call activity
 * sheet writes an event's date over its time.
 */
export const dateAndSeconds = (iso: string): { date: string; time: string } => {
  const at = new Date(iso);
  return {
    date: at.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }),
    time: at.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }),
  };
};

export interface DutyStatus {
  value: string;
  label: string;
  tone: StatusTone;
}

/**
 * What a driver can set themselves, through PUT /driver-mobile/status. The
 * endpoint also takes CLEAR and DISPATCHED, which follow from their calls
 * rather than from a choice.
 */
export const DUTY_STATUSES: DutyStatus[] = [
  { value: 'ACTIVE', label: 'On duty', tone: 'success' },
  { value: 'ON_LEAVE', label: 'Off duty', tone: 'neutral' },
];

const DUTY_WORDS: Record<string, string> = {
  ACTIVE: 'On duty',
  ON_LEAVE: 'Off duty',
  CLEAR: 'Clear',
  DISPATCHED: 'On a call',
};

export function dutyLabel(status: string | null | undefined): string {
  if (!status) return 'Off duty';
  return DUTY_WORDS[status] ?? sentenceCase(status);
}

export function dutyTone(status: string | null | undefined): StatusTone {
  if (status === 'ACTIVE' || status === 'CLEAR') return 'success';
  if (status === 'DISPATCHED') return 'phase-dispatched';
  return 'neutral';
}

/** Minutes as a phone says them: "42 min", "1h 5m", "2h". */
export function minutesLabel(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes))
    return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

/** A driver's truck by its number: "Truck 4", or "No truck". */
export const truckLabel = (truckNumber: string | null | undefined) =>
  truckNumber ? `Truck ${truckNumber}` : 'No truck';
